/**
 * A2UIChatGlobal - Vanilla JS wrapper for A2UIChat
 * 
 * Provides an imperative API for non-React usage:
 * - Browser extensions
 * - Standalone web pages
 * - Iframe embeds
 * 
 * Usage:
 *   const chat = new A2UIChatGlobal({
 *     apiEndpoint: 'https://api.example.com',
 *     workspaceId: 'ws-123',
 *     canvasId: 'canvas-456',
 *     accessToken: 'token-xyz',
 *     layoutMode: 'full', // 'compact' | 'full'
 *     theme: 'dark',      // 'dark' | 'light'
 *     onMessageSent: (msg) => console.log('Sent:', msg),
 *     onTaskCreated: (task) => console.log('Task:', task),
 *     onError: (err) => console.error('Error:', err),
 *   });
 *   
 *   await chat.init(document.getElementById('container'));
 *   chat.setLayoutMode('compact');
 *   chat.setTheme('light');
 *   chat.destroy();
 * 
 * @module A2UIChatGlobal
 */

import { A2UIChat } from './A2UIChat.js';

export class A2UIChatGlobal {
    /**
     * @param {Object} options
     * @param {string} options.apiEndpoint - API base URL
     * @param {string} options.workspaceId - Workspace ID
     * @param {string} options.canvasId - Canvas ID
     * @param {string} options.accessToken - Authentication token
     * @param {string} options.layoutMode - 'compact' or 'full'
     * @param {string} options.theme - 'dark' or 'light'
     * @param {Function} options.onMessageSent - Callback when message is sent
     * @param {Function} options.onTaskCreated - Callback when task is created
     * @param {Function} options.onError - Callback on error
     * @param {Function} options.onDataChanged - Callback when agent completes (for graph reload)
     */
    constructor(options = {}) {
        this.options = {
            apiEndpoint: null,
            workspaceId: null,
            canvasId: null,
            accessToken: null,
            layoutMode: 'full',
            theme: 'dark',
            onMessageSent: null,
            onTaskCreated: null,
            onError: null,
            onDataChanged: null,
            ...options
        };
        
        this.container = null;
        this.chat = null;
        this._initialized = false;
    }
    
    /**
     * Initialize the chat component in a container element
     * @param {HTMLElement} container - DOM element to render into
     * @returns {Promise<A2UIChatGlobal>}
     */
    async init(container) {
        if (this._initialized) {
            console.warn('[A2UIChatGlobal] Already initialized');
            return this;
        }
        
        this.container = container;
        
        // Create the A2UIChat instance
        this.chat = new A2UIChat({
            apiEndpoint: this.options.apiEndpoint,
            workspaceId: this.options.workspaceId,
            canvasId: this.options.canvasId,
            accessToken: this.options.accessToken,
            layoutMode: this.options.layoutMode,
            theme: this.options.theme,
            onMessageSent: this.options.onMessageSent,
            onTaskCreated: this.options.onTaskCreated,
            onError: this.options.onError,
            onDataChanged: this.options.onDataChanged,
        });
        
        // Initialize
        await this.chat.init(container);
        
        this._initialized = true;
        
        // Initialize Lucide icons if available
        this._refreshIcons();
        
        return this;
    }
    
    /**
     * Refresh Lucide icons in the container
     */
    _refreshIcons() {
        if (typeof lucide !== 'undefined' && lucide.createIcons) {
            lucide.createIcons({
                icons: lucide.icons,
                attrs: {
                    class: ['lucide-icon'],
                },
            });
        }
    }
    
    /**
     * Set the layout mode
     * @param {string} mode - 'compact' or 'full'
     */
    setLayoutMode(mode) {
        this.options.layoutMode = mode;
        if (this.chat) {
            this.chat.setLayoutMode(mode);
            this._refreshIcons();
        }
    }
    
    /**
     * Set the theme
     * @param {string} theme - 'dark' or 'light'
     */
    setTheme(theme) {
        this.options.theme = theme;
        if (this.chat) {
            this.chat.setTheme(theme);
        }
    }
    
    /**
     * Set the canvas ID
     * @param {string} canvasId
     */
    setCanvasId(canvasId) {
        this.options.canvasId = canvasId;
        if (this.chat) {
            this.chat.setCanvasId(canvasId);
        }
    }
    
    /**
     * Set the workspace ID (requires re-initialization)
     * @param {string} workspaceId
     */
    async setWorkspaceId(workspaceId) {
        this.options.workspaceId = workspaceId;
        if (this.chat && this.container) {
            // Re-initialize with new workspace
            this.destroy();
            await this.init(this.container);
        }
    }
    
    /**
     * Send a message programmatically
     * @param {string} content - Message content
     * @param {Array} entities - Optional entities to include
     */
    async sendMessage(content, entities = []) {
        if (!this.chat) {
            console.warn('[A2UIChatGlobal] Not initialized');
            return;
        }
        await this.chat.sendMessage(content, entities);
    }
    
    /**
     * Clear the conversation
     */
    clearConversation() {
        if (this.chat) {
            this.chat.clearConversation();
        }
    }
    
    /**
     * Refresh all data (tasks, artifacts, agents)
     */
    async refresh() {
        if (this.chat) {
            await this.chat.refresh();
        }
    }
    
    /**
     * Get current messages
     * @returns {Array}
     */
    getMessages() {
        return this.chat?.messages || [];
    }
    
    /**
     * Get current tasks
     * @returns {Array}
     */
    getTasks() {
        return this.chat?.tasks || [];
    }
    
    /**
     * Get current artifacts
     * @returns {Array}
     */
    getArtifacts() {
        return this.chat?.artifacts || [];
    }
    
    /**
     * Check if initialized
     * @returns {boolean}
     */
    isInitialized() {
        return this._initialized;
    }
    
    /**
     * Destroy the component and cleanup
     */
    destroy() {
        if (this.chat) {
            this.chat.destroy();
            this.chat = null;
        }
        this._initialized = false;
    }
}

// Export for global access in non-module contexts
if (typeof window !== 'undefined') {
    window.A2UIChatGlobal = A2UIChatGlobal;
}

export default A2UIChatGlobal;
